import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
    Zap, Search, Loader2, AppWindow, ShieldAlert, KeyRound, Sparkles, BookMarked,
    Users, FileSpreadsheet, FileText, Skull, Flame,
} from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { ModuleGate } from "@/components/common/ModuleGate";
import { SeverityBadge } from "@/components/common/Badges";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

export default function InstantAnalysis() {
    // Deep-links (command palette, dashboard chips) arrive as /instant-analysis?q=FB60
    const [searchParams] = useSearchParams();
    const [query, setQuery] = useState(searchParams.get("q") || "");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    // Instant SoD Analyser state
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [extraTcodes, setExtraTcodes] = useState("");
    const [sodResult, setSodResult] = useState(null);
    const [sodLoading, setSodLoading] = useState(false);
    const [exporting, setExporting] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/roles");
                setRoles(data);
            } catch (e) {
                console.warn("Failed to load roles for SoD Analyser", e);
            }
        })();
    }, []);

    const run = useCallback(async (raw) => {
        const term = (typeof raw === "string" ? raw : query).trim();
        if (!term) { toast.error("Enter a T-code or Fiori App ID"); return; }
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/instant-analysis", { query: term });
            setResult(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Analysis failed");
        } finally { setLoading(false); }
    }, [query]);

    // Auto-run once when the page is opened with ?q=
    const deepLink = searchParams.get("q");
    useEffect(() => {
        if (deepLink) run(deepLink);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLink]);

    const toggleRole = (id) => {
        setSelectedRoles((prev) => prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]);
    };

    const buildSodPayload = () => ({
        role_ids: selectedRoles,
        extra_label: "Ad-hoc Simulation",
        extra_tcodes: extraTcodes.split(/[,;\s]+/).map((t) => t.trim().toUpperCase()).filter(Boolean),
    });

    const runSodAnalysis = async () => {
        const payload = buildSodPayload();
        if (payload.role_ids.length === 0 && payload.extra_tcodes.length === 0) {
            toast.error("Select at least one Role or enter T-codes to simulate"); return;
        }
        setSodLoading(true);
        setSodResult(null);
        try {
            const { data } = await api.post("/instant-analysis/sod", payload);
            setSodResult(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "SoD analysis failed");
        } finally { setSodLoading(false); }
    };

    const exportSod = async (fmt) => {
        const payload = buildSodPayload();
        if (payload.role_ids.length === 0 && payload.extra_tcodes.length === 0) {
            toast.error("Run an analysis first"); return;
        }
        setExporting(fmt);
        try {
            const { data } = await api.post(`/instant-analysis/sod/export/${fmt}`, payload, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `instant_sod_analysis.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded ${fmt.toUpperCase()} report`);
        } catch (e) {
            toast.error("Export failed");
        } finally { setExporting(""); }
    };

    return (
        <ModuleGate module="instant_analysis" label="Instant Analysis">
        <div className="space-y-6">
            <div className="panel-elevated relative overflow-hidden p-6">
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />
                <div className="flex items-center gap-4 relative">
                    <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_20px_rgb(var(--accent-1-rgb) / 0.45)]">
                        <Zap size={22} className="text-slate-950" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl text-white tracking-wide">Instant Analysis</h1>
                        <p className="text-sm text-slate-400 mt-0.5">
                            AI-powered instant lookup of any SAP T-code or Fiori App — offline Fiori Apps Library + real GRC SoD ruleset + the UTSAVA Reasoner.
                        </p>
                    </div>
                </div>
            </div>

            <Panel eyebrow="Query" title="Analyze a T-code or Fiori App ID">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            data-testid={TID.instantAnalysisInput}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && run()}
                            placeholder="e.g. FB60, ME21N, F110, F7319…"
                            className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm pl-9 pr-3 py-2.5 text-sm font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                        />
                    </div>
                    <button
                        data-testid={TID.instantAnalysisSubmit}
                        onClick={run}
                        disabled={loading}
                        className="btn-primary sheen inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {loading ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
                        {loading ? "Analyzing…" : "Analyze"}
                    </button>
                </div>
            </Panel>

            {result && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid={TID.instantAnalysisResult} className="space-y-6">
                    {result.ai_summary && (
                        <Panel eyebrow="UTSAVA Reasoner · Executive Narrative" title="AI Summary">
                            <div className="flex items-start gap-2">
                                <Sparkles size={15} className="text-cyan-400 shrink-0 mt-0.5" />
                                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{result.ai_summary}</p>
                            </div>
                        </Panel>
                    )}

                    <Panel eyebrow="Fiori Apps Reference Library" title={`Matched apps (${result.matched_apps.length})`}>
                        {result.matched_apps.length === 0 ? (
                            <p className="text-sm text-slate-500 font-mono py-3 text-center">No match in the offline Fiori Apps Library for &ldquo;{result.query}&rdquo;.</p>
                        ) : (
                            <div className="space-y-2">
                                {result.matched_apps.map((a) => (
                                    <div key={a.app_id} className="panel p-3 rounded-[4px] flex items-center gap-3">
                                        <AppWindow size={14} className="text-cyan-400 shrink-0" />
                                        <span className="font-mono text-xs text-cyan-300 shrink-0">{a.app_id}</span>
                                        <span className="text-sm text-slate-200 flex-1 truncate">{a.app_name}</span>
                                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-400 border border-white/10 shrink-0">{a.module_name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Panel>

                    <Panel eyebrow="Real SAP GRC Ruleset" title={`Segregation-of-Duties risks (${result.risks.length})`}>
                        {result.risks.length === 0 ? (
                            <p className="text-sm text-emerald-400/90 font-mono py-3 text-center">No SoD risk pairing detected for this T-code alone.</p>
                        ) : (
                            <div className="space-y-2">
                                {result.risks.map((r) => (
                                    <div key={r.risk_id} className="panel-elevated p-3 border-l-2 border-rose-500/50">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <ShieldAlert size={13} className="text-rose-400" />
                                            <SeverityBadge severity={r.severity} />
                                            <span className="text-xs text-white font-medium">{r.name}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400 mt-1">{r.description}</p>
                                        <div className="text-[10px] font-mono text-slate-500 mt-1">conflicts with: {r.conflicting_function}</div>
                                        {r.suggested_controls?.length > 0 && (
                                            <div className="mt-2 pt-2 border-t border-white/5 flex flex-wrap gap-1">
                                                {r.suggested_controls.map((s) => (
                                                    <span key={s.control_id} title={s.title} className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                                                        <BookMarked size={9} /> {s.control_id}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Panel>

                    {result.critical_activities.length > 0 && (
                        <Panel eyebrow="Standalone Critical Activities" title={`${result.critical_activities.length} found`}>
                            <div className="space-y-2">
                                {result.critical_activities.map((c) => (
                                    <div key={c.risk_id} className="panel-elevated p-3 border-l-2 border-orange-500/50">
                                        <div className="flex items-center gap-2"><SeverityBadge severity={c.severity} /><span className="text-xs text-white font-medium">{c.name}</span></div>
                                        <p className="text-[11px] text-slate-400 mt-1">{c.description}</p>
                                    </div>
                                ))}
                            </div>
                        </Panel>
                    )}

                    <Panel eyebrow="Inferred Authorization Objects" title={`${result.auth_objects.length} objects`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <KeyRound size={13} className="text-slate-500" />
                            {result.auth_objects.map((o) => (
                                <span key={o} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">{o}</span>
                            ))}
                        </div>
                    </Panel>
                </motion.div>
            )}

            {/* Instant SoD Analyser — simulate a Role/User combo + ad-hoc extras */}
            <Panel
                eyebrow="Ad-hoc Simulation"
                title={<span className="inline-flex items-center gap-1.5"><Users size={14} className="text-rose-400" /> Instant SoD Analyser</span>}
            >
                <p className="text-xs text-slate-500 mb-4">
                    Select one or more saved Roles to simulate a USER holding all of them together, and/or add extra T-codes to test
                    the impact of a proposed access change — then export a board-ready Excel or PDF report.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <div className="label-eyebrow mb-1.5">Roles to combine ({selectedRoles.length} selected)</div>
                        <div className="relative">
                            <div className="max-h-[184px] overflow-y-auto space-y-1 panel p-2 pb-5 rounded-[4px]">
                                {roles.length === 0 && <p className="text-xs text-slate-500 font-mono py-2 text-center">No roles in the library yet.</p>}
                                {roles.map((r) => (
                                    <label key={r.id} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-white/5 cursor-pointer text-xs">
                                        <input
                                            type="checkbox"
                                            data-testid={TID.sodAnalyserRoleCheckbox(r.id)}
                                            checked={selectedRoles.includes(r.id)}
                                            onChange={() => toggleRole(r.id)}
                                            className="accent-cyan-500"
                                        />
                                        <span className="font-mono text-cyan-300/90 truncate flex-1">{r.name}</span>
                                        {r.sod_conflicts?.length > 0 && <span className="text-[10px] font-mono text-rose-400">{r.sod_conflicts.length} SoD</span>}
                                    </label>
                                ))}
                            </div>
                            {roles.length > 6 && (
                                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[var(--bg-surface)] to-transparent rounded-b-[4px]" />
                            )}
                        </div>
                        {roles.length > 6 && (
                            <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                                Scroll for all {roles.length} roles
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="label-eyebrow mb-1.5">Ad-hoc extra T-codes to simulate (optional)</div>
                        <textarea
                            data-testid={TID.sodAnalyserExtraTcodesInput}
                            value={extraTcodes}
                            onChange={(e) => setExtraTcodes(e.target.value)}
                            rows={3}
                            placeholder="e.g. FB60, F-53, ME21N (comma or space separated)"
                            className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm px-3 py-2 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none resize-y"
                        />
                        <button
                            data-testid={TID.sodAnalyserRunBtn}
                            onClick={runSodAnalysis}
                            disabled={sodLoading}
                            className="btn-primary sheen mt-2 w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {sodLoading ? <Loader2 size={14} className="animate-spin" /> : <Flame size={14} />}
                            {sodLoading ? "Analyzing…" : "Run SoD Analysis"}
                        </button>
                    </div>
                </div>

                {sodResult && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={TID.sodAnalyserResult} className="mt-5 pt-5 border-t border-white/10 space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <SeverityBadge severity={sodResult.risk_level} />
                                <span className="text-xs font-mono text-slate-400">
                                    {sodResult.entities.join(" + ")} · {sodResult.combined_tcodes.length} tcodes · {sodResult.sod_conflicts.length} SoD · {sodResult.critical_access.length} critical
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    data-testid={TID.sodAnalyserExportBtn("xlsx")}
                                    onClick={() => exportSod("xlsx")}
                                    disabled={exporting !== ""}
                                    className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-50"
                                >
                                    {exporting === "xlsx" ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel
                                </button>
                                <button
                                    data-testid={TID.sodAnalyserExportBtn("pdf")}
                                    onClick={() => exportSod("pdf")}
                                    disabled={exporting !== ""}
                                    className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-50"
                                >
                                    {exporting === "pdf" ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} PDF
                                </button>
                            </div>
                        </div>

                        {sodResult.sod_conflicts.length === 0 && sodResult.critical_access.length === 0 ? (
                            <p className="text-sm text-emerald-400/90 font-mono py-3 text-center">Clean combination — no SoD conflicts or critical activities detected.</p>
                        ) : (
                            <div className="space-y-2">
                                {sodResult.sod_conflicts.map((c, i) => (
                                    <div key={i} className="panel-elevated p-3 border-l-2 border-rose-500/50">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <ShieldAlert size={13} className="text-rose-400" />
                                            <SeverityBadge severity={c.severity} />
                                            <span className="text-xs text-white font-medium">{c.rule_name}</span>
                                            {c.cross_role && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-orange-500/10 text-orange-300 border border-orange-500/20">CROSS-ROLE</span>}
                                        </div>
                                        <div className="text-[11px] text-slate-400 mt-1">
                                            {c.function_a} ({c.roles_a.join(", ")}) ↔ {c.function_b} ({c.roles_b.join(", ")})
                                        </div>
                                    </div>
                                ))}
                                {sodResult.critical_access.map((c, i) => (
                                    <div key={i} className="panel-elevated p-3 border-l-2 border-orange-500/50">
                                        <div className="flex items-center gap-2"><Skull size={13} className="text-orange-400" /><SeverityBadge severity={c.severity} /><span className="text-xs text-white font-medium">{c.name}</span></div>
                                        <p className="text-[11px] text-slate-400 mt-1">{c.description}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </Panel>
        </div>
        </ModuleGate>
    );
}
